import React, { useState } from 'react';
import { Briefcase, Calendar, Clock, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';

import { BACKEND_URL } from '../config/api';
import { todayYMD } from '../utils/dateHelpers';

export default function HireModal({ open, onClose, tradesperson, onSuccess }) {
  const [startDate, setStartDate] = useState('');
  const [durationWeeks, setDurationWeeks] = useState(1);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!startDate) {
      toast.error('Please select a start date');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/employments`,
        {
          tradesperson_id: tradesperson.user_id,
          start_date: startDate,
          duration_weeks: durationWeeks,
          notes: notes || null
        },
        { withCredentials: true }
      );
      
      toast.success(`Marked ${tradesperson.user_info?.name || 'tradesperson'} as hired!`);
      onSuccess?.(response.data);
      onClose();
      
      // Reset form
      setStartDate('');
      setDurationWeeks(1);
      setNotes('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create employment record');
    } finally {
      setLoading(false);
    }
  };

  // Calculate end date for display
  const calculateEndDate = () => {
    if (!startDate) return null;
    const start = new Date(startDate);
    start.setDate(start.getDate() + (durationWeeks * 7));
    return start.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="hire-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Briefcase className="h-5 w-5 text-orange-500" />
            Mark as Hired
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          {/* Tradesperson Info */}
          <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
            <p className="font-medium text-slate-900">{tradesperson?.user_info?.name}</p>
            <p className="text-sm text-orange-600">{tradesperson?.trade}</p>
            <p className="text-sm text-slate-500">£{tradesperson?.daily_rate}/day</p>
          </div>

          {/* Start Date */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              <Calendar className="h-4 w-4 inline mr-1" />
              Start Date *
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              required
              className="w-full"
              min={todayYMD()}
              data-testid="hire-start-date"
            />
          </div>

          {/* Duration */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              <Clock className="h-4 w-4 inline mr-1" />
              Duration *
            </label>
            <select
              value={durationWeeks}
              onChange={(e) => setDurationWeeks(parseFloat(e.target.value))}
              className="w-full"
              data-testid="hire-duration"
            >
              <optgroup label="Days">
                <option value={0.14}>1 day</option>
                <option value={0.29}>2 days</option>
                <option value={0.43}>3 days</option>
                <option value={0.57}>4 days</option>
                <option value={0.71}>5 days</option>
              </optgroup>
              <optgroup label="Weeks">
                <option value={1}>1 week</option>
                <option value={2}>2 weeks</option>
                <option value={3}>3 weeks</option>
                <option value={4}>4 weeks (1 month)</option>
                <option value={6}>6 weeks</option>
                <option value={8}>8 weeks (2 months)</option>
                <option value={12}>12 weeks (3 months)</option>
                <option value={26}>26 weeks (6 months)</option>
                <option value={52}>52 weeks (1 year)</option>
              </optgroup>
            </select>
          </div>

          {/* End Date Preview */}
          {startDate && (
            <div className="p-3 bg-green-50 rounded-lg border border-green-200">
              <p className="text-sm text-green-800">
                <strong>Estimated End Date:</strong> {calculateEndDate()}
              </p>
              <p className="text-xs text-green-600 mt-1">
                You'll receive a reminder to leave a review when the job ends.
              </p>
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Notes (Optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="w-full"
              placeholder="e.g., Kitchen renovation project"
              data-testid="hire-notes"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary flex-1"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary flex-1 flex items-center justify-center gap-2"
              disabled={loading}
              data-testid="confirm-hire-btn"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              ) : (
                <>
                  <Briefcase className="h-4 w-4" />
                  Confirm Hire
                </>
              )}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
